/**
 * Calculation Reference module — ordered, read-only catalogue of every
 * canonical flow-metric definition delivered by PRODOPSAI-410.
 *
 * Both backend (US3 invariant tests) and frontend (US7 admin page) consume the
 * exports below. Keep the array order stable: it is the rendering order on the
 * Calculation Reference admin page.
 */

import type { ICalculationDefinition } from './types.js';
import { CONCEPTS } from './concepts.js';

export type {
  CalculationCategory,
  ICalculationDefinition,
  IDecision,
  ICaveat,
  IPbiComparisonEntry,
  IPbiComparisonForConcept,
  ISourceTableEntry,
} from './types.js';

export { DECISIONS } from './decisions.js';
export { CAVEATS } from './caveats.js';
export { PBI_COMPARISON } from './pbi-comparison.js';
export { SOURCE_TABLES } from './source-tables.js';

/**
 * The canonical repository method names that back the documented concepts.
 * US3 (PRODOPSAI-413) shipped the original flow methods; PRODOPSAI-441 added
 * the SonarCloud / fabric methods that back the quality concepts. The
 * invariant test only verifies that every `repositoryMethod` string here
 * matches one of these names.
 */
export const CANONICAL_REPOSITORY_METHODS: ReadonlyArray<string> = [
  // Flow metrics (PRODOPSAI-413).
  'getCurrentWipCount',
  'getCurrentWipPerTeam',
  'getCurrentWipPerEpic',
  'getCurrentWipAvgAge',
  'getWipAgingDistribution',
  'getWipTrend',
  'getOpenIssueCount',
  // Code-quality metrics (PRODOPSAI-441).
  'getRepoSnapshot',
  'getTrendSeries',
  'getLatestPreWindowCoverageByProject',
  'getByTeam',
  'findLatestSuccessful',
] as const;

/**
 * Ordered list of canonical calculation definitions. Order matches the
 * rendering order on the Calculation Reference admin page (logical reading
 * order: WIP → trend → age → aging → open → total → cancelled → throughput).
 */
export const CALCULATION_REFERENCE: ReadonlyArray<ICalculationDefinition> = CONCEPTS;

/**
 * Lookup helper used by the admin page renderer. Thin wrapper to keep call
 * sites from re-implementing the same `find` everywhere.
 */
export function findCalculationDefinition(
  conceptKey: string,
): ICalculationDefinition | undefined {
  return CALCULATION_REFERENCE.find((entry) => entry.conceptKey === conceptKey);
}
